import React, { useMemo, useState } from 'react';
import { useIntl } from 'umi';
import { Bubble, Button, Popover, DropdownBox, List, SelectOption } from 'tea-component';
import useDebounceHandler from './hooks/useDebounceHandler';
import { DOWNLOAD_TYPES } from '@/pages/intelligence/pages/TopCharts/_config/topChartsConst';
import type { IBaseProps } from '@/pages/intelligence/types/common';

interface IMultiTypeDownloadBtnProps extends IBaseProps {
  // 下载按钮是否开启 hover 浮窗提示文案信息
  isHoverMsg?: boolean;
  // 下载按钮hover的提示文案
  hoverMsg?: string | React.ReactElement;
  // 是否开启click回调 防抖，默认关闭
  isDebounce?: boolean;
  // 是否下载loading状态
  isLoading?: boolean;
  // 是否禁用下载按钮
  isDisabled?: boolean;
  // 下载类型选项，默认支持 下载EChart、下载Excel（CSV） 两种类型选择，单一类型可以不传options，组件调用者自行判断 下载的是什么类型文件
  options?: SelectOption[];
  // 点击下载按钮的回调，单一类型时
  onClick?: (e?: React.MouseEvent<Element, MouseEvent>) => void;
  // 选择下载类型的回调
  onSelect?: (payload: string) => void;
}

export default function MultiTypeDownloadBtn(props: IMultiTypeDownloadBtnProps) {
  const { formatMessage } = useIntl();

  const {
    isHoverMsg = false,
    hoverMsg = formatMessage({ id: 'dbi_info_download_tip' }),
    isDebounce = false,
    isLoading = false,
    isDisabled = false,
    options = DOWNLOAD_TYPES,
    onSelect,
    onClick,
  } = props;

  const btnIcon = useMemo<'loading' | 'download'>(() => !!isLoading ? 'loading' : 'download', [isLoading]);

  const [visible, setVisible] = useState(false);

  const { handler: handleClick } = useDebounceHandler({
    isDebounce,
    onHandler: onClick,
  });

  const { handler: handleSelect } = useDebounceHandler({ isDebounce, onHandler: onSelect });

  const renderOption = (option: SelectOption) => {
    const optionText = option?.text;
    if (typeof optionText === 'string') return formatMessage({ id: option?.text as string });
    return option?.text;
  };

  return (
    <Bubble placement="top-end" dark content={isHoverMsg ? hoverMsg : undefined}>
      {/* 单一类型下载 */}
      {options?.length > 1 || (
        <Button
          icon={btnIcon}
          className="ns-btn--icon trend-justify-btn"
          loading={isLoading}
          disabled={isDisabled}
          onClick={(e) => {
            e?.stopPropagation();
            e?.preventDefault();
            handleClick(e);
          }}
        />
      )}
      {/* 多类型选择下载 */}
      {options?.length > 1 && (
        <Popover
          trigger="click"
          placement="bottom-start"
          closeOnScroll
          visible={visible}
          onVisibleChange={(visible) => setVisible(visible)}
          overlay={
            <DropdownBox>
              <List type="option">
                {options?.map((option) => (
                  <List.Item
                    key={option?.value}
                    onClick={(e) => {
                      e?.stopPropagation();
                      e?.preventDefault();
                      handleSelect(option?.value);
                      setVisible(false);
                    }}
                  >
                    {renderOption(option)}
                  </List.Item>
                ))}
              </List>
            </DropdownBox>
          }
        >
          <Button icon={btnIcon} className="ns-btn--icon trend-justify-btn" loading={isLoading} disabled={isDisabled} />
        </Popover>
      )}
    </Bubble>
  );
}
